import Vue from "vue";
import App from "./App.vue";
import vuetify from "./plugins/vuetify";
import router from "./router";
import store from "./store";
import * as Sentry from "@sentry/browser";
import * as Integrations from "@sentry/integrations";
import LogRocket from "logrocket";
import { getVersion } from "@/api/api";
import VueAnalytics from "vue-analytics";
import VueCompositionApi from "@vue/composition-api";
import { axiosBase } from "./api/axios-base";

const canonicalLoginPath = "/app/auth/login";
const legacyRecoverMatch = window.location.pathname.match(/^\/auth\/recover\/(.+)$/);

if (legacyRecoverMatch) {
  window.location.replace(
    `/app/auth/recover/${legacyRecoverMatch[1]}${window.location.search}${window.location.hash}`,
  );
}

Vue.use(VueCompositionApi);

if (process.env.NODE_ENV !== "development") {
  Vue.use(VueAnalytics, {
    id: process.env.VUE_APP_GOOGLE_ANALYTICS_ID,
    router,
  });

  LogRocket.init("aat5yc/arches", {
    dom: {
      inputSanitizer: true,
    },
  });

  Sentry.init({
    dsn: process.env.VUE_APP_SENTRY_DSN,
    integrations: [new Integrations.Vue({ Vue, attachProps: true })],
    beforeSend(event) {
      const logRocketSession = LogRocket.sessionURL;
      if (logRocketSession !== null) {
        event.extra["LogRocket"] = logRocketSession;
        return event;
      } else {
        return event;
      }
    },
  });

  LogRocket.getSessionURL((sessionURL) => {
    Sentry.configureScope((scope) => {
      scope.setExtra("sessionURL", sessionURL);
    });
  });
}

Vue.config.productionTip = false;

const initRouter = () => {
  router.beforeEach(async (to, from, next) => {
    if (process.env.NODE_ENV !== "development") {
      getVersion()
        .then((response) => {
          if (response.data.version !== process.env.VUE_APP_RELEASE_VERSION) {
            // eslint-disable-next-line no-console
            console.log(
              `You are running app version ${process.env.VUE_APP_RELEASE_VERSION}. Version ${response.data.version} is available!!! Feel free to refresh and download it.`,
            );
            store.commit("snackVersion", true);
          }
        })
        .catch(() => {});
    }
    if (store.state.profile?.role === "EDITOR" && store.getters.loggedIn) {
      if (to.matched.some((r) => r.meta.editorCan)) {
        next();
      } else {
        next({ name: "admin-lessons" });
      }
    } else if (store.state.profile?.role === "ASSISTANT" && store.getters.loggedIn) {
      if (to.matched.some((record) => record.meta.assistantCan)) {
        next();
      } else {
        next({ name: "admin-active-contacts" });
      }
    } else if (to.matched.some((record) => record.meta.requiresAuth)) {
      if (!store.getters.loggedIn) {
        store.commit("next", to);
        next(false);
        window.location.assign(canonicalLoginPath);
      } else {
        next();
      }
    } else if (to.matched.some((record) => record.meta.requiresLogged)) {
      if (store.getters.loggedIn) {
        next({ name: "modules" });
      } else {
        next();
      }
    } else {
      next();
    }
  });
};

const app = new Vue({
  router,
  store,
  vuetify,
  render: (h) => h(App),
});

/**
 * Never mount the SPA when we are sending the browser to Nuxt login.
 * If we mount first, the router guard hits `requiresAuth` with no session,
 * calls `next(false)`, and aborts navigation — leaving an empty `router-view`
 * (blank page) until/unless the document redirect completes.
 */
if (!legacyRecoverMatch) {
  axiosBase
    .post("/api/auth/refresh-token/")
    .then(async () => {
      await store.dispatch("getProfile");
      if (!store.getters.loggedIn) {
        await store.dispatch("logoutUser");
        if (router.currentRoute.name !== "login") {
          window.location.replace(canonicalLoginPath);
          return "redirect";
        }
      }
      return "mount";
    })
    .catch(() => {
      store.dispatch("logoutUser");
      window.location.replace(canonicalLoginPath);
      return "redirect";
    })
    .then((action) => {
      if (action === "redirect") {
        return;
      }
      initRouter();
      app.$mount("#app");
    });
}
